import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { useGridCopyMenu } from './gridCommon';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

export type FutType = 'stk' | 'idx';

interface Props {
  /** stk = 주식선물, idx = 지수선물 */
  futType: FutType;
}

interface MasterRow {
  prodid: string;
  prodtp: string;
  prodnm: string;
  stdcd: string | null;
  matmon: string | null;
  monthcls: string | null;
  prodcls: string | null;
  undlyprodid: string | null;
  undlyprodnm: string | null;
  isspread: boolean;
  isoption: boolean;
  rtsubscribable: boolean;
  mktcls?: string | null;
  ismini?: boolean;
  isweekly?: boolean;
}

interface DailyRow {
  prodid: string;
  d: string;
  openprc: number | null;
  highprc: number | null;
  lowprc: number | null;
  clsprc: number | null;
  vol: number | null;
  amt: number | null;
}

/** 차트 기간 — 기본 1년 */
type Period = '1M' | '3M' | '6M' | '1Y' | 'ALL';
const PERIODS: { key: Period; label: string; days: number | null }[] = [
  { key: '1M', label: '1개월', days: 30 },
  { key: '3M', label: '3개월', days: 90 },
  { key: '6M', label: '6개월', days: 180 },
  { key: '1Y', label: '1년', days: 365 },
  { key: 'ALL', label: '전체', days: null },
];

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '2px solid #3b82f6',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const cardTitleStyle: React.CSSProperties = {
  marginBottom: 8, fontSize: 12, color: '#1e40af', fontWeight: 600,
};

const chipBtn = (active: boolean): React.CSSProperties => ({
  padding: '3px 10px',
  fontSize: 11,
  fontWeight: active ? 700 : 500,
  color: active ? 'white' : '#1e40af',
  background: active ? '#3b82f6' : 'white',
  border: '1px solid #3b82f6',
  borderRadius: 4,
  cursor: 'pointer',
});

const num = (v: any) => (v == null ? '' : Number(v).toLocaleString());
const fmtD = (s: string | null) => (s ? s.slice(0, 10) : '');

/** X축 날짜 라벨 YY.MM.DD (프로젝트 공통 규칙) */
const axisDateLabel = (ms: number) => {
  const d = new Date(ms);
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
};

const FuturesData: React.FC<Props> = ({ futType }) => {
  const axios = getMainAxios();
  const copyMenu = useGridCopyMenu();

  const [mstD, setMstD] = useState<string | null>(null);
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [nearOnly, setNearOnly] = useState(true);

  const [selected, setSelected] = useState<MasterRow | null>(null);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [period, setPeriod] = useState<Period>('1Y'); // 디폴트 1년

  const label = futType === 'idx' ? '지수선물' : '주식선물';

  const fetchMaster = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/v1/data/futures/master', {
        params: { type: futType, search: search || undefined, nearOnly },
      });
      setMstD(r.data?.d ?? null);
      setRows(r.data?.rows ?? []);
    } catch (e) {
      console.error('[FuturesData] 마스터 조회 실패', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [axios, futType, search, nearOnly]);

  const fetchDaily = useCallback(async (prodId: string) => {
    setDailyLoading(true);
    try {
      const r = await axios.get('/api/v1/data/futures/daily', {
        params: { type: futType, prodId },
      });
      setDaily(r.data ?? []);
    } catch (e) {
      console.error('[FuturesData] 일별시세 조회 실패', e);
      setDaily([]);
    } finally {
      setDailyLoading(false);
    }
  }, [axios, futType]);

  useEffect(() => {
    setSelected(null);
    setDaily([]);
  }, [futType]);

  useEffect(() => { fetchMaster(); }, [fetchMaster]);

  // 목록 로드 후 선택 종목이 없으면 첫 행 자동 선택
  useEffect(() => {
    if (rows.length > 0 && !selected) {
      setSelected(rows[0]);
      fetchDaily(rows[0].prodid);
    }
  }, [rows, selected, fetchDaily]);

  const onSelect = (r: MasterRow) => {
    setSelected(r);
    fetchDaily(r.prodid);
  };

  // 기간 필터 적용
  const periodDaily = useMemo(() => {
    const days = PERIODS.find(p => p.key === period)?.days ?? null;
    if (days == null) return daily;
    const cutoff = Date.now() - days * 86400000;
    return daily.filter(r => new Date(r.d.slice(0, 10) + 'T00:00:00').getTime() >= cutoff);
  }, [daily, period]);

  const masterCols = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
      { headerName: '종목코드', field: 'prodid', width: 100,
        cellStyle: { fontFamily: 'monospace', fontWeight: 600 } },
      { headerName: '종목명', field: 'prodnm', flex: 1, minWidth: 150 },
      { headerName: '만기', field: 'matmon', width: 80, cellStyle: { textAlign: 'center' } },
      { headerName: '기초자산', field: 'undlyprodnm', width: 120 },
    ];
    return cols;
  }, []);

  const dailyCols = useMemo<ColDef[]>(() => [
    { headerName: '일자', field: 'd', width: 100, valueFormatter: p => fmtD(p.value),
      cellStyle: { textAlign: 'center' } },
    { headerName: '시가', field: 'openprc', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '고가', field: 'highprc', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '저가', field: 'lowprc', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '종가', field: 'clsprc', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 } },
    { headerName: '거래량', field: 'vol', flex: 1, minWidth: 95, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '거래대금', field: 'amt', flex: 1, minWidth: 115, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
  ], []);

  const chartOptions = useMemo<Highcharts.Options>(() => {
    const src = periodDaily.filter(r => r.clsprc != null);
    const candle = src.map(r => [
      new Date(r.d.slice(0, 10) + 'T00:00:00').getTime(),
      Number(r.openprc ?? r.clsprc), Number(r.highprc ?? r.clsprc),
      Number(r.lowprc ?? r.clsprc), Number(r.clsprc),
    ]);
    const volume = src.map(r => [
      new Date(r.d.slice(0, 10) + 'T00:00:00').getTime(), Number(r.vol ?? 0),
    ]);

    return {
      chart: { height: 360 },
      title: { text: undefined },
      credits: { enabled: false },
      rangeSelector: { enabled: false },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      xAxis: {
        type: 'datetime',
        labels: { formatter(this: any) { return axisDateLabel(this.value as number); } },
        dateTimeLabelFormats: {
          day: '%y.%m.%d', week: '%y.%m.%d', month: '%y.%m.%d', year: '%y.%m.%d',
        } as any,
      },
      yAxis: [
        { labels: { align: 'right', x: -3 }, title: { text: undefined },
          height: '70%', lineWidth: 1, resize: { enabled: true } },
        { labels: { align: 'right', x: -3 }, title: { text: undefined },
          top: '74%', height: '26%', offset: 0, lineWidth: 1 },
      ],
      tooltip: { split: true },
      series: [
        {
          type: 'candlestick', name: selected?.prodnm ?? '', data: candle,
          color: '#1d4ed8', upColor: '#dc2626',
        } as Highcharts.SeriesCandlestickOptions,
        {
          type: 'column', name: '거래량', data: volume, yAxis: 1, color: '#94a3b8',
        } as Highcharts.SeriesColumnOptions,
      ],
    };
  }, [periodDaily, selected]);

  return (
    <div style={{ padding: 16, backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 상단 검색 바 */}
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: 14, fontWeight: 600 }}>
            {label} 데이터
          </h3>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') fetchMaster(); }}
            placeholder="종목코드 / 종목명 / 기초자산"
            style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, width: 240 }}
          />
          <label style={{ fontSize: 12, color: '#334155', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={nearOnly} onChange={e => setNearOnly(e.target.checked)} />
            최근월물 단일선물만
          </label>
          <button
            onClick={fetchMaster}
            disabled={loading}
            style={{
              padding: '4px 12px', background: '#3b82f6', color: 'white', border: 'none',
              borderRadius: 4, cursor: loading ? 'wait' : 'pointer', fontSize: 12, fontWeight: 500,
            }}
          >
            {loading ? '⏳ 조회 중...' : '🔍 조회'}
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
            마스터 기준일: {fmtD(mstD)} · {rows.length.toLocaleString()}종목
          </span>
        </div>
      </div>

      {/* 본문: 좌(종목마스터) / 우(차트 + 일별시세) */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* 좌 — 종목마스터 */}
        <div style={{ ...cardStyle, flex: '0 0 460px', marginBottom: 0 }}>
          <div style={cardTitleStyle}>
            종목마스터 ({rows.length.toLocaleString()}건)
          </div>
          <div className="ag-theme-quartz" style={{ height: 760, width: '100%', fontSize: 12 }}>
            <AgGridReact<MasterRow>
              rowData={rows}
              columnDefs={masterCols}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              onRowClicked={e => { if (e.data) onSelect(e.data); }}
              getRowStyle={p => (p.data?.prodid === selected?.prodid ? { backgroundColor: '#dbeafe' } : undefined)}
              rowHeight={30}
              headerHeight={32}
              {...copyMenu.gridProps}
            />
          </div>
        </div>

        {/* 우 — 차트 + 일별시세 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* 차트 */}
          <div style={{ ...cardStyle, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ ...cardTitleStyle, marginBottom: 0 }}>
                차트 {selected ? `— ${selected.prodid} ${selected.prodnm}` : ''}
              </span>
              <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                {PERIODS.map(p => (
                  <button key={p.key} style={chipBtn(period === p.key)} onClick={() => setPeriod(p.key)}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {!selected ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
                좌측에서 종목을 선택하세요.
              </div>
            ) : periodDaily.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
                {dailyLoading ? '조회 중...'
                  : daily.length === 0
                    ? '일별시세 데이터가 없습니다. (수집 대상 종목이 아닐 수 있습니다)'
                    : '선택한 기간에 데이터가 없습니다.'}
              </div>
            ) : (
              <HighchartsReact highcharts={Highcharts} constructorType="stockChart" options={chartOptions} />
            )}
          </div>

          {/* 일별시세 */}
          <div style={{ ...cardStyle, marginBottom: 0 }}>
            <div style={cardTitleStyle}>
              일별시세 {selected ? `— ${selected.prodid}` : ''}
              {dailyLoading ? ' (조회 중...)' : ` (${periodDaily.length}건)`}
              {periodDaily.length > 0 &&
                ` · ${fmtD(periodDaily[0].d)} ~ ${fmtD(periodDaily[periodDaily.length - 1].d)}`}
            </div>
            <div className="ag-theme-quartz" style={{ height: 340, width: '100%', fontSize: 12 }}>
              <AgGridReact<DailyRow>
                rowData={[...periodDaily].reverse()}
                columnDefs={dailyCols}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                rowHeight={28}
                headerHeight={32}
                {...copyMenu.gridProps}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuturesData;
